import React, { useContext, useState, useCallback } from 'react';
import { Loader2Icon, ShieldCheckIcon, XCircleIcon, ExternalLinkIcon, CheckCircleIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ToolCallHandlersContext } from './chart-tool-call';
import { ConnectorIcon } from './connector-icon';
import { useSource } from '@/hooks/useSource';
import { useSourceTypes } from '@/hooks/useSourceTypes';
import ApiService from '@/services/api.service';

interface OAuthAuthorizeToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete' | 'interrupted'; reason?: string };
}

export const OAuthAuthorizeToolCall: React.FC<OAuthAuthorizeToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const handlers = useContext(ToolCallHandlersContext);
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Parse source_id from args
  let sourceId = '';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    sourceId = parsedArgs?.source_id || '';
  } catch (e) {
    // Ignore
  }

  // Parse from result (interrupt value or completion value)
  let resultSourceName = '';
  let resultConnectorTitle = '';
  let resultConnectorType = '';
  let isAwaitingAuth = false;
  let isAuthorized = false;
  if (result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      sourceId = parsed.source_id || sourceId;
      resultSourceName = parsed.source_name || '';
      resultConnectorTitle = parsed.connector_title || '';
      resultConnectorType = parsed.connector_type || '';

      if (parsed?.status === 'awaiting_authorization') {
        isAwaitingAuth = true;
      } else if (parsed?.status === 'authorized' || parsed?.already_authorized) {
        isAuthorized = true;
      }
    } catch (e) {
      // Ignore
    }
  }

  // Look up source details from sourceId — works even when result/interrupt data is missing
  const { data: source } = useSource(sourceId || undefined);
  const { data: sourceTypes } = useSourceTypes();
  const sourceType = sourceTypes?.find(st => st.id === source?.type);

  const connectorType = resultConnectorType || source?.type || '';
  const connectorTitle = resultConnectorTitle || sourceType?.title || '';
  const sourceName = resultSourceName || source?.name || '';

  // Detect authorized from source data if result didn't tell us
  if (!isAuthorized && source?.oauth_last_authorized) {
    isAuthorized = true;
  }

  // Show spinner until interrupt data arrives with connector info
  const isRunning = status?.type === 'running';
  const isInterrupted = status?.type === 'interrupted';

  // Handle authorize button click — navigates away to OAuth provider
  const handleAuthorize = useCallback(async () => {
    if (!sourceId) {
      setError('No source ID available');
      return;
    }

    setIsRedirecting(true);
    setError(null);

    try {
      const threadId = handlers?.threadId;
      let returnUrl = '';
      if (threadId) {
        returnUrl = `${window.location.origin}/onboarding/conversation/${threadId}?authorized=true&source_id=${sourceId}`;
      }

      const queryParam = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : '';
      const resp = await ApiService.getInstance().request(
        'GET',
        `/sources/connectors/source/${sourceId}/authorization-url${queryParam}`
      ) as { record: { authorization_url: string } };

      const authUrl = resp.record.authorization_url;
      if (!authUrl) {
        setError('Failed to get authorization URL');
        setIsRedirecting(false);
        return;
      }

      window.location.href = authUrl;
    } catch (err) {
      console.error('[OAuthAuthorizeToolCall] Authorization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start authorization');
      setIsRedirecting(false);
    }
  }, [sourceId, handlers]);

  if (error) {
    return (
      <Card>
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
            <p className="text-sm m-0 text-destructive">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleAuthorize}>
            Try again
          </Button>
        </div>
      </Card>
    );
  }

  if (isAuthorized) {
    return (
      <Card>
        <div className="p-3 bg-muted/50 border-b border-border">
          <div className="flex items-center gap-2">
            {connectorType && <ConnectorIcon connectorType={connectorType} size={20} />}
            <p className="text-sm m-0 text-foreground">
              {sourceName || connectorTitle || 'Connector'} — Authorized
            </p>
            <CheckCircleIcon className="h-4 w-4 text-success flex-shrink-0 ml-auto" />
          </div>
        </div>
      </Card>
    );
  }

  if (isRedirecting) {
    return (
      <Card>
        <div className="p-3">
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            {connectorType && <ConnectorIcon connectorType={connectorType} size={18} />}
            <p className="text-sm m-0 text-foreground">
              Redirecting to {connectorTitle || 'provider'} for authorization...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (isRunning) {
    return (
      <Card>
        <div className="p-3">
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              Preparing authorization...
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Awaiting auth, interrupted, or fallback (e.g. reloaded thread with pending auth)
  return (
    <Card>
      <div className="p-3 space-y-3">
        <div className="flex items-center gap-2">
          {connectorType ? (
            <ConnectorIcon connectorType={connectorType} size={20} />
          ) : (
            <ShieldCheckIcon className="h-4 w-4 text-primary flex-shrink-0" />
          )}
          <p className="text-sm m-0 text-foreground">
            <strong>{connectorTitle || 'Connector'}</strong> requires authorization
          </p>
        </div>
        <p className="text-xs text-muted-foreground m-0">
          Click below to open the authorization page. You will be redirected to
          {connectorTitle ? ` ${connectorTitle}'s` : " the provider's"} website to grant access.
          After authorizing, you'll be brought back to this conversation.
        </p>
        <Button onClick={handleAuthorize} size="sm">
          {connectorType ? (
            <ConnectorIcon connectorType={connectorType} size={16} className="mr-1" />
          ) : (
            <ExternalLinkIcon className="h-3 w-3 mr-1" />
          )}
          Authorize {connectorTitle || 'Connector'}
        </Button>
      </div>
    </Card>
  );
};
